<script lang="ts" setup>
import { computed, type PropType } from 'vue';

import LocalTime from '@exchange/libs/components/history/LocalTime.vue';
import type { PricetickModel } from '@exchange/libs/price-ticks/service/src';

const props = defineProps({
  /** Price tick to show */
  priceTick: { type: Object as PropType<PricetickModel>, required: true },
  /** Defines if arrow should be shown */
  showArrow: { type: Boolean, required: true },
  /** Market amount precision */
  amountPrecision: { type: Number, required: true },
  /** Market market precision */
  marketPrecision: { type: Number, required: true },
});

const timeFormat = 'HH:mm:ss';

const amount = computed(() => props.priceTick.amount.toFixed(props.amountPrecision));
const price = computed(() => props.priceTick.price.toFixed(props.marketPrecision));
const isoTime = props.priceTick.time?.toISOString();
</script>

<template>
  <tr
    class="price-ticks-history-item"
    :data-time="isoTime"
    :data-timeS="priceTick.timeString"
    :class="[priceTick.isBuy ? 'price-ticks-history-item--buy' : 'price-ticks-history-item--sell']"
  >
    <td class="price-ticks-history-item__cell price-ticks-history-item__cell--side">
      <template v-if="showArrow">
        <ot-icon
          class="price-ticks-history-item__icon"
          :name="priceTick.isBuy ? 'arrow-up' : 'arrow-down'"
        />
      </template>
    </td>
    <td class="price-ticks-history-item__cell price-ticks-history-item__cell--amount">
      <span>{{ amount }}</span>
    </td>
    <td class="price-ticks-history-item__cell price-ticks-history-item__cell--price">
      <span>{{ price }}</span>
    </td>
    <td class="price-ticks-history-item__cell price-ticks-history-item__cell--time">
      <local-time
        :time="priceTick.time"
        :format="timeFormat"
      />
    </td>
  </tr>
</template>

<style lang="scss">
@keyframes highlight-price-tick {
  from {
    background-color: var(--price-tick-item-background-color);
  }

  to {
    background-color: var(--row-background-color);
  }
}

.price-ticks-history-item {
  --arrow-icon-width: 16px;

  animation: highlight-price-tick 400ms ease-out;
  overflow-x: hidden;
  text-align: right;

  .xs & {
    height: 20px;
    background: rgb(var(--v-theme-elevation-1));
  }

  &__cell {
    position: relative;
    z-index: 1;
    overflow: hidden;

    &--price {
      color: var(--price-tick-item-price-color);
    }

    &--time {
      color: rgb(var(--v-theme-text-tertiary));
    }
  }

  &__icon {
    width: var(--arrow-icon-width);
    color: var(--price-tick-item-arrow-color);

    .xs & {
      --arrow-icon-width: 12px;
    }
  }
}

.price-ticks-history-item--buy {
  --price-tick-item-background-color: var(--success-darkest);
  --price-tick-item-arrow-color: rgb(var(--v-theme-success));
  --price-tick-item-price-color: rgb(var(--v-theme-success));
}

.price-ticks-history-item--sell {
  --price-tick-item-background-color: var(--error-darkest);
  --price-tick-item-arrow-color: rgb(var(--v-theme-error));
  --price-tick-item-price-color: rgb(var(--v-theme-error));
}
</style>
